// src/stores/game.js
import { defineStore } from 'pinia'
import { ref, computed, reactive } from 'vue'
import { api } from '@/utils/api'
import { notify } from '@kyvg/vue3-notification'
import { moneyInCents, roundMoney } from '../utils/gameplay.js'

export const useGameStore = defineStore('game', () => {
  // ─── State ─────────────────────────────────────
  const user                = ref(null)
  const game                = ref(null)
  const race                = ref(null)
  const money               = ref([])       // [ { code, name, value }, … ]
  const gameTime            = ref(0)        // seconds
  const timeZone            = ref('UTC')
  const timeZoneOffset      = ref(0)        // minutes
  const lastTimeZoneOffset  = ref(0)
  const currentCountryCode  = ref('GB')
  const gameSpeed           = ref(1)
  const lastSavedGameTime   = ref(0)        // for hourly “ping” saves
  const journeyCount        = ref(0)     // track the number of journeys in the game

  const saving              = ref(false)    // for UI feedback during save operations
  const showSettingsDialog  = ref(true)
  const gameSettings        = reactive({
    showTrainStations: true,
    showBusStations: true,
    showFerryStations: true,
    showJobs: true,
    showHotels: true,
    showBanks: true,
  })

  // ─── Getters ───────────────────────────────────
  const daysPassed = computed(() => Math.floor(gameTime.value / 86400))
  const dayOfWeek  = computed(() => {
    const days = ['Monday','Tuesday','Wednesday','Thursday','Friday','Saturday','Sunday']
    return days[daysPassed.value % 7]
  })
  const dayOfWeekInt = computed(() => (daysPassed.value % 7) + 1)

  // ─── Actions ───────────────────────────────────
  function setUser(u)               { user.value = u }
  function setGame(g)               { game.value = g }
  function setRace(r)               { race.value = r }
  function setMoney(m)              { money.value = m.map(entry => ({ ...entry, value: roundMoney(entry.value) })) }
  function setGameTime(t)           { gameTime.value = Number(t) }
  function setTimeZone(tz)          { timeZone.value = tz }
  function setTimeZoneOffset(off)   { timeZoneOffset.value = off }
  function setLastTimeZoneOffset(o) { lastTimeZoneOffset.value = o }
  function setCurrentCountryCode(c) { currentCountryCode.value = c }
  function setGameSpeed(s)          { gameSpeed.value = s }
  function setLastSavedGameTime(t)  { lastSavedGameTime.value = t }
  function incrementJourneyCount()   { journeyCount.value++ }
  function setShowSettingsDialog(v)  { showSettingsDialog.value = v }
  function toggleShowSettingsDialog(){ showSettingsDialog.value = !showSettingsDialog.value }
  function updateGameSettings(settings) {
    Object.assign(gameSettings, settings)
  }

  /** Advance in‑game clock by `seconds`. */
  function tickTime(seconds) {
    gameTime.value += seconds
  }

  /** Add to your wallet. */
  function addMoney(amount, code, symbol = null) {
    amount = roundMoney(amount)
    if (!Number.isFinite(amount) || amount < 0) return false
    const entry = money.value.find(m => m.code === code)
    if (entry) {
      entry.value = roundMoney(entry.value + amount)
    } else {
      money.value.push({ code, name: code, symbol, value: amount })
    }

    notify({ title: 'Money Added', text: `+${amount} ${code}`, type: 'success' })
  }

  /** Deduct from wallet, with error if insufficient. */
  function deductMoney(amount, code, allowNegative = false) {
    amount = roundMoney(amount)
    if (!Number.isFinite(amount) || amount < 0) return false
    const entry = money.value.find(m => m.code === code)

    if (!entry) {
      notify({ title: 'Currency Missing', text: `No ${code} in wallet`, type: 'error' })
      return false
    }
    if (!allowNegative && moneyInCents(entry.value) < moneyInCents(amount)) {
      notify({ title: 'Insufficient Funds', text: `Need ${amount} ${code}`, type: 'error' })
      return false
    }
    entry.value = (moneyInCents(entry.value) - moneyInCents(amount)) / 100
    return true
  }

  /**
   * Save (or create) the current game on the server.
   * @param {number} lat  – current player latitude
   * @param {number} lng  – current player longitude
   */
  function saveGame(lat, lng) {
    return new Promise((resolve, reject) => {
      const params = { lat, lng, time: gameTime.value }
      if (game.value?.id) {
        api({
          method: 'POST',
          endpoint: `api/games/${game.value.id}`,
          params,
          successCallback: () => {
            lastSavedGameTime.value = gameTime.value
            resolve()
          },
          errorCallback: err => {
            err?.errors?.forEach(e => 
              notify({ title: 'Save Error', text: e.message, type: 'error' })
            )
            reject(err)
          }
        })
      } else {
        params.race_id = race.value.id
        api({
          method: 'POST',
          endpoint: 'api/games',
          params,
          successCallback: res => {
            game.value = res.data.data
            lastSavedGameTime.value = gameTime.value
            resolve(res)
          },
          errorCallback: err => {
            err?.errors?.forEach(e => 
              notify({ title: 'Save Error', text: e.message, type: 'error' })
            )
            reject(err)
          }
        })
      }
    })
  }

  /**
   * Log an event (walk, taxi, ping…) to the server.
   * `params` should include whatever your backend needs:
   *  { event, to_lat, to_lng, start, end, money?, currency? }
   */
  function logGame(params, successLogCallback, errorLogCallback) {
    if (!game.value?.id) return

    saving.value = true;

    const defaultParams = {
        time: gameTime.value,
    };

    // This is a big hacky.  It's because I don't have access to gameTime in useRouting
    // where I call logGame() for Walk and Taxi events.  So I pass the duration
    // in `params.end` and compute start/end here.  Ugh.
    if(params.event === 'Walk' || params.event === 'Taxi') {
        params.start = gameTime.value;
        params.end = gameTime.value + params.end;
    }

    const payload = { ...defaultParams, ...params }
    api({
      method: 'POST',
      endpoint: `api/games/${game.value.id}/log`,
      params: payload,
      successCallback: () => {
        saving.value = false;
        if (typeof successLogCallback === 'function') successLogCallback();
      },
      errorCallback: err => {
        saving.value = false;
        err?.errors?.forEach(e =>
          notify({ title: 'Log Error', text: e.message, type: 'error' })
        )
        if (typeof errorLogCallback === 'function') errorLogCallback(err);
      }
    })
  }

  function resetGame(params, successLogCallback, errorLogCallback) {
    api({
      method: 'POST',
      endpoint: `api/games/reset`,
      params,
      successCallback: () => {
        saving.value = false;
        if (typeof successLogCallback === 'function') successLogCallback();
      },
      errorCallback: err => {
        saving.value = false;
        err?.errors?.forEach(e =>
          notify({ title: 'Log Error', text: e.message, type: 'error' })
        )
        if (typeof errorLogCallback === 'function') errorLogCallback(err);
      }
    })
  }

  return {
    // state
    user, game, race, money,
    gameTime, timeZone, timeZoneOffset, lastTimeZoneOffset,
    currentCountryCode, gameSpeed, lastSavedGameTime, saving, journeyCount,
    showSettingsDialog, gameSettings,
    // getters
    daysPassed, dayOfWeek, dayOfWeekInt,
    // actions
    setUser, setGame, setRace, setMoney,
    setGameTime, setTimeZone, setTimeZoneOffset, setLastTimeZoneOffset,
    setCurrentCountryCode, setGameSpeed, setLastSavedGameTime,
    tickTime, addMoney, deductMoney, saveGame, logGame, resetGame, incrementJourneyCount,
    setShowSettingsDialog, toggleShowSettingsDialog, updateGameSettings
  }
})
